import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { PermissionGuard } from "../../components/PermissionGuard";
import QuickBookMappingForm from "./QuickBookMappingForm";
import { QuickBookService } from "./Service";
import { quickBookStatusLabel } from "./display";

const ok = (response) => response?.success === 1 || response?.success === "1";
const emptyPagination = { page: 0, pageSize: 20 };
const EmptyRows = ({ label }) => (
  <Box sx={{ display: "grid", height: "100%", placeItems: "center", p: 2 }}>
    <Typography color="text.secondary">{label}</Typography>
  </Box>
);

const readinessLabel = (value) => value ? "Ready" : "Not ready";
const expertInitials = (name) => String(name || "Expert")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase();

export const toExpertSettingPayload = (expert, values) => ({
  expectedVersion: expert.version,
  isEligible: values.status === "ENABLED",
  status: values.status,
  reason: values.status === "SUSPENDED" ? String(values.reason || "").trim() : null,
});

export const isExpertSettingEditUnsafe = (expert) => (
  Boolean(expert) && !Number.isSafeInteger(expert.version)
);

const EligibilityDialog = ({ expert, open, onClose, onSaved, onConflict }) => {
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const { control, handleSubmit, reset, setError, watch } = useForm({
    defaultValues: { status: "OFF", reason: "" },
  });
  const status = watch("status");

  useEffect(() => {
    if (!open || !expert) return;
    setMessage(null);
    reset({ status: expert.status || "OFF", reason: expert.reason || "" });
  }, [expert, open, reset]);

  const submit = async (values) => {
    setSaving(true);
    setMessage(null);
    const response = await QuickBookService.updateExpertSetting(
      expert.providerId,
      toExpertSettingPayload(expert, values),
    );
    setSaving(false);
    if (ok(response)) {
      onSaved();
      return;
    }
    (response?.error?.fieldErrors || []).forEach(({ field, message }) => setError(field, { type: "server", message }));
    const stale = response?.error?.code === "QUICK_BOOK_CONFIG_VERSION_CHANGED";
    if (stale) onConflict();
    setMessage({ severity: stale || response?.error?.retryable ? "warning" : "error", text: stale ? "This eligibility setting changed. Close and reopen it before retrying." : response?.message || "Eligibility could not be updated." });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="eligibility-title">
      <DialogTitle id="eligibility-title">Quick Book eligibility · {expert?.displayName || `Expert #${expert?.providerId}`}</DialogTitle>
      <DialogContent dividers>
        <Box id="eligibility-form" component="form" onSubmit={handleSubmit(submit)} noValidate>
          <Stack spacing={2}>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            <Alert severity="info">This global gate is independent of service mappings. Changing it will not create, activate, or delete mappings.</Alert>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth label="Global status">
                  <MenuItem value="OFF">Off</MenuItem>
                  <MenuItem value="ENABLED" disabled={!expert?.accountActive}>Enabled</MenuItem>
                  <MenuItem value="SUSPENDED">Suspended</MenuItem>
                </TextField>
              )}
            />
            {status === "SUSPENDED" && (
              <Controller
                name="reason"
                control={control}
                rules={{ required: "Suspension reason is required", maxLength: { value: 300, message: "Maximum 300 characters" }, validate: (value) => !/[<>]/.test(value) || "Plain text only" }}
                render={({ field, fieldState }) => <TextField {...field} fullWidth multiline minRows={2} label="Suspension reason" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 300 }} />}
              />
            )}
            <Typography color="text.secondary" sx={{ fontSize: ".75rem" }}>Current version: {expert?.version ?? 0}</Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" form="eligibility-form" variant="contained" disabled={saving || !expert || isExpertSettingEditUnsafe(expert)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default function QuickBookExperts() {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [experts, setExperts] = useState([]);
  const [expertCount, setExpertCount] = useState(0);
  const [expertLoading, setExpertLoading] = useState(false);
  const [expertError, setExpertError] = useState("");
  const [expertPagination, setExpertPagination] = useState(emptyPagination);
  const [expertFilters, setExpertFilters] = useState({ search: "", status: "" });
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [mappingCount, setMappingCount] = useState(0);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState("");
  const [mappingPagination, setMappingPagination] = useState(emptyPagination);
  const [mappingFilters, setMappingFilters] = useState({ serviceId: "", providerId: "", active: "" });
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [quickBookServices, setQuickBookServices] = useState([]);
  const [quickBookServicesLoading, setQuickBookServicesLoading] = useState(true);
  const [mappingExperts, setMappingExperts] = useState([]);
  const [mappingExpertSearch, setMappingExpertSearch] = useState("");
  const [quickBookServicesError, setQuickBookServicesError] = useState("");
  const expertForm = useForm({ defaultValues: expertFilters });
  const mappingForm = useForm({ defaultValues: mappingFilters });

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-experts" });
  }, [dispatch]);

  const fetchExperts = useCallback(async () => {
    setExpertLoading(true);
    setExpertError("");
    const response = await QuickBookService.expertSettings({
      page: expertPagination.page + 1,
      limit: expertPagination.pageSize,
      ...expertFilters,
    });
    if (ok(response)) {
      setExperts(response.data?.items || []);
      setExpertCount(response.data?.pagination?.totalItems || 0);
    } else {
      setExperts([]);
      setExpertCount(0);
      setExpertError(response?.message || "Expert eligibility could not be loaded.");
    }
    setExpertLoading(false);
  }, [expertFilters, expertPagination]);

  const fetchMappings = useCallback(async () => {
    setMappingLoading(true);
    setMappingError("");
    const response = await QuickBookService.mappings({
      page: mappingPagination.page + 1,
      limit: mappingPagination.pageSize,
      ...mappingFilters,
    });
    if (ok(response)) {
      setMappings(response.data?.items || []);
      setMappingCount(response.data?.pagination?.totalItems || 0);
    } else {
      setMappings([]);
      setMappingCount(0);
      setMappingError(response?.message || "Quick Book mappings could not be loaded.");
    }
    setMappingLoading(false);
  }, [mappingFilters, mappingPagination]);

  useEffect(() => { fetchExperts(); }, [fetchExperts]);
  useEffect(() => { fetchMappings(); }, [fetchMappings]);
  useEffect(() => {
    setQuickBookServicesLoading(true);
    setQuickBookServicesError("");
    QuickBookService.list({ page: 1, limit: 100, search: "", mode: "", status: "" }).then((response) => {
      if (ok(response)) {
        setQuickBookServices(response.data?.items || []);
      } else {
        setQuickBookServices([]);
        setQuickBookServicesError(response?.message || "Quick Book service options could not be loaded.");
      }
      setQuickBookServicesLoading(false);
    });
  }, []);

  useEffect(() => {
    if (tab !== 1) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      const response = await QuickBookService.expertSettings({ page: 1, limit: 50, search: mappingExpertSearch.trim(), status: "" });
      if (active) setMappingExperts(ok(response) ? response.data?.items || [] : []);
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [mappingExpertSearch, tab]);

  const expertColumns = useMemo(() => [
    {
      field: "displayName",
      headerName: "Expert",
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ width: 38, height: 38, bgcolor: "primary.main", fontSize: ".8rem", fontWeight: 700 }}>{expertInitials(row.displayName)}</Avatar>
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography sx={{ fontSize: ".8rem", fontWeight: 700 }}>{row.displayName || "Expert"}</Typography>
              <Chip size="small" variant="outlined" color="primary" label={`#${row.providerId}`} sx={{ height: 20, fontSize: ".65rem" }} />
            </Stack>
            <Typography color="text.secondary" sx={{ fontSize: ".7rem", mt: 0.25 }}>{row.mobileNumber || "No mobile number"}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: "readiness",
      headerName: "Backend readiness",
      minWidth: 280,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, max-content)", gap: 0.65 }}>
          {[
            ["Account", row.accountActive],
            ["Review", row.readiness?.reviewApproved],
            ["KYC", row.readiness?.kycVerified],
            ["PAN", row.readiness?.panVerified],
          ].map(([label, value]) => <Chip key={label} size="small" variant={value ? "outlined" : "filled"} color={value ? "success" : "warning"} label={`${label}: ${readinessLabel(value)}`} sx={{ height: 24, fontSize: ".68rem" }} />)}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Global Quick Book status",
      minWidth: 220,
      renderCell: ({ row }) => (
        <Box sx={{ px: 1.25, py: 0.75, borderRadius: 2, bgcolor: row.status === "ENABLED" ? "#ecfdf3" : row.status === "SUSPENDED" ? "#fffaeb" : "grey.100" }}>
          <Chip size="small" color={row.status === "ENABLED" ? "success" : row.status === "SUSPENDED" ? "warning" : "default"} label={quickBookStatusLabel(row.status)} />
          <Typography color="text.secondary" sx={{ fontSize: ".65rem", mt: 0.25 }}>
            {row.status === "ENABLED" ? `Enabled — ${row.mappedServiceCount} mapped service${row.mappedServiceCount === 1 ? "" : "s"}` : row.status === "SUSPENDED" ? row.reason : row.status === "OFF" ? "Off" : "Status unavailable"}
          </Typography>
        </Box>
      ),
    },
    { field: "version", headerName: "Version", minWidth: 80, type: "number", renderCell: ({ value }) => <Chip size="small" variant="outlined" label={`v${value}`} /> },
    {
      field: "schedule",
      headerName: "Schedule",
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => <Button size="small" variant="outlined" startIcon={<CalendarMonthOutlinedIcon />} href={`/admin/userProfile/${row.providerId}/4`} target="_blank" rel="noreferrer" sx={{ whiteSpace: "nowrap" }}>Schedule</Button>,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <PermissionGuard feature="service-expert" permission="canEdit">
          <Tooltip title="Edit global eligibility"><IconButton aria-label={`Edit eligibility for ${row.displayName || row.providerId}`} size="small" color="warning" onClick={() => setSelectedExpert(row)} sx={{ bgcolor: "#fff7ed", "&:hover": { bgcolor: "#ffedd5" } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
        </PermissionGuard>
      ),
    },
  ], []);

  const mappingColumns = useMemo(() => [
    {
      field: "serviceTitle",
      headerName: "Quick Book service",
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => <Box><Typography sx={{ fontSize: ".75rem", fontWeight: 600 }}>{row.serviceTitle}</Typography><Typography color="text.secondary" sx={{ fontSize: ".65rem" }}>{row.serviceMode}</Typography></Box>,
    },
    {
      field: "providerDisplayName",
      headerName: "Expert / provider service",
      minWidth: 190,
      renderCell: ({ row }) => <Box><Typography sx={{ fontSize: ".75rem" }}>{row.providerDisplayName || "Expert"} #{row.providerId}</Typography><Typography color="text.secondary" sx={{ fontSize: ".65rem" }}>Provider service #{row.providerServiceId}</Typography></Box>,
    },
    {
      field: "coverage",
      headerName: "Coverage",
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => <Typography sx={{ fontSize: ".7rem" }}>Address #{row.coverageCenterAddressId} · {row.coverageRadiusKm} km</Typography>,
    },
    { field: "priority", headerName: "Priority", minWidth: 85, type: "number" },
    {
      field: "providerUnitPayoutPaise",
      headerName: "Unit payout",
      minWidth: 115,
      renderCell: ({ value, row }) => <Chip size="small" color={row.negativeMargin ? "warning" : "default"} label={`₹${(Number(value || 0) / 100).toFixed(2)}`} />,
    },
    {
      field: "controls",
      headerName: "Controls",
      minWidth: 230,
      sortable: false,
      renderCell: ({ row }) => <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}><Chip size="small" color={row.active ? "success" : "default"} label={row.active ? "Active" : "Inactive"} /><Chip size="small" variant="outlined" label={row.payoutEligible ? "Payout eligible" : "Payout blocked"} />{row.scheduleManagedByCudel && <Chip size="small" color="warning" label="CuDel-managed schedule" />}</Box>,
    },
    {
      field: "updatedAt",
      headerName: "Updated / version",
      minWidth: 170,
      renderCell: ({ row }) => <Box><Typography sx={{ fontSize: ".7rem" }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}</Typography><Typography color="text.secondary" sx={{ fontSize: ".65rem" }}>v{row.version}{row.updatedBy ? ` · Admin #${row.updatedBy}` : ""}</Typography></Box>,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <PermissionGuard feature="service-expert" permission="canEdit">
          <Tooltip title="Edit mapping"><IconButton aria-label={`Edit mapping ${row.mappingId}`} size="small" color="warning" onClick={() => { setSelectedMapping(row); setMappingDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
        </PermissionGuard>
      ),
    },
  ], []);

  const applyExpertFilters = (values) => {
    setExpertPagination((current) => ({ ...current, page: 0 }));
    setExpertFilters({ search: String(values.search || "").trim(), status: values.status });
  };
  const applyMappingFilters = (values) => {
    setMappingPagination((current) => ({ ...current, page: 0 }));
    setMappingFilters({ serviceId: values.serviceId, providerId: String(values.providerId || "").trim(), active: values.active });
  };

  return (
    <>
      <PageHeaderLayout headerTitle="Quick Book Experts & Mappings" />
      <Card sx={{ mb: 1 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Quick Book expert configuration">
          <Tab label="Global eligibility" />
          <Tab label="Service mappings" />
        </Tabs>
      </Card>

      {tab === 0 && (
        <Stack spacing={1}>
          <Card component="form" onSubmit={expertForm.handleSubmit(applyExpertFilters)} sx={{ p: 1.5 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={6}><Controller name="search" control={expertForm.control} render={({ field }) => <TextField {...field} fullWidth size="small" label="Search expert name or mobile" />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><Controller name="status" control={expertForm.control} render={({ field }) => <TextField {...field} select fullWidth size="small" label="Status"><MenuItem value="">All</MenuItem>{["OFF", "ENABLED", "SUSPENDED"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>} /></Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", gap: 1 }}><Button type="submit" variant="contained" size="small" startIcon={<SearchIcon />} fullWidth>Search</Button><Tooltip title="Refresh"><IconButton aria-label="Refresh expert eligibility" onClick={fetchExperts} disabled={expertLoading}><RefreshIcon /></IconButton></Tooltip></Grid>
            </Grid>
          </Card>
          {expertError && <Alert severity="error">{expertError}</Alert>}
          <Card variant="outlined" sx={{ overflowX: "auto", borderRadius: 2, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)" }}><Box sx={{ minWidth: 1100 }}><DataGrid autoHeight rows={experts} columns={expertColumns} getRowId={(row) => row.providerId} getRowHeight={() => "auto"} getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? "expert-row-even" : "expert-row-odd"} loading={expertLoading} rowCount={expertCount} paginationMode="server" paginationModel={expertPagination} onPaginationModelChange={setExpertPagination} pageSizeOptions={[10, 20, 50, 100]} disableRowSelectionOnClick slots={{ noRowsOverlay: () => <EmptyRows label="No experts found." /> }} sx={{ border: 0, "& .MuiDataGrid-columnHeaders": { bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }, "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 }, "& .MuiDataGrid-cell": { minHeight: 76, py: 1.25, borderColor: "grey.200" }, "& .expert-row-even": { bgcolor: "common.white" }, "& .expert-row-odd": { bgcolor: "grey.50" }, "& .MuiDataGrid-row:hover": { bgcolor: "#eff6ff" }, "& .MuiDataGrid-footerContainer": { bgcolor: "grey.50" } }} /></Box></Card>
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={1}>
          <Card component="form" onSubmit={mappingForm.handleSubmit(applyMappingFilters)} sx={{ p: 1.5 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={4}><Controller name="serviceId" control={mappingForm.control} render={({ field }) => <TextField {...field} select fullWidth size="small" label="Quick Book service" disabled={quickBookServicesLoading || Boolean(quickBookServicesError)}><MenuItem value="">All</MenuItem>{quickBookServices.map((service) => <MenuItem key={service.serviceId} value={service.serviceId}>{service.customerTitle}</MenuItem>)}</TextField>} /></Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Controller name="providerId" control={mappingForm.control} render={({ field }) => (
                  <Autocomplete
                    options={mappingExperts}
                    value={mappingExperts.find((expert) => String(expert.providerId) === String(field.value)) || null}
                    getOptionLabel={(expert) => `${expert.displayName || "Expert"} (#${expert.providerId})`}
                    isOptionEqualToValue={(option, value) => Number(option.providerId) === Number(value.providerId)}
                    onInputChange={(_, value, reason) => reason === "input" && setMappingExpertSearch(value)}
                    onChange={(_, value) => field.onChange(value?.providerId || "")}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" label="Search expert name or ID" />}
                  />
                )} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}><Controller name="active" control={mappingForm.control} render={({ field }) => <TextField {...field} select fullWidth size="small" label="Mapping"><MenuItem value="">All</MenuItem><MenuItem value="true">Active</MenuItem><MenuItem value="false">Inactive</MenuItem></TextField>} /></Grid>
              <Grid item xs={12} sm={4} md={4} sx={{ display: "flex", gap: 1 }}><Button type="submit" variant="contained" size="small" startIcon={<SearchIcon />} fullWidth>Search</Button><Tooltip title="Refresh"><IconButton aria-label="Refresh mappings" onClick={fetchMappings} disabled={mappingLoading}><RefreshIcon /></IconButton></Tooltip><PermissionGuard feature="service-expert" permission="canCreate"><Button size="small" variant="contained" color="secondary" startIcon={<AddCircleIcon />} disabled={quickBookServicesLoading || Boolean(quickBookServicesError) || quickBookServices.length === 0} onClick={() => { setSelectedMapping(null); setMappingDialogOpen(true); }} fullWidth>Add mapping</Button></PermissionGuard></Grid>
            </Grid>
          </Card>
          <Alert severity="info">Global eligibility and service mappings are separate controls. A provider must be globally enabled and have a valid active mapping before the backend can offer Quick Book work.</Alert>
          {quickBookServicesError && <Alert severity="error">Service options unavailable: {quickBookServicesError}</Alert>}
          {mappingError && <Alert severity="error">{mappingError}</Alert>}
          <Card sx={{ overflowX: "auto" }}><Box sx={{ minWidth: 1250 }}><DataGrid autoHeight rows={mappings} columns={mappingColumns} getRowId={(row) => row.mappingId} loading={mappingLoading} rowCount={mappingCount} paginationMode="server" paginationModel={mappingPagination} onPaginationModelChange={setMappingPagination} pageSizeOptions={[10, 20, 50, 100]} disableRowSelectionOnClick slots={{ noRowsOverlay: () => <EmptyRows label="No Quick Book mappings found." /> }} sx={{ border: 0, "& .MuiDataGrid-cell": { py: 1 }, "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 } }} /></Box></Card>
        </Stack>
      )}

      <EligibilityDialog expert={selectedExpert} open={Boolean(selectedExpert)} onClose={() => setSelectedExpert(null)} onSaved={() => { setSelectedExpert(null); fetchExperts(); }} onConflict={fetchExperts} />
      <QuickBookMappingForm open={mappingDialogOpen} mapping={selectedMapping} onClose={() => setMappingDialogOpen(false)} onSaved={() => { setMappingDialogOpen(false); fetchMappings(); fetchExperts(); }} onConflict={fetchMappings} />
    </>
  );
}
